var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ImageTypeNameEnum, ItemTypeEnum, SheetAlignmentEnum } from '@inter-ikea-kompis/enums';
import { DexfSettingsUtility, ProductDescriptionProductMeasureEnum, ProductDescriptionUtility, ProductUtility } from '@inter-ikea-kompis/utilities';
import { ActionList, ActionListItem } from '@inter-ikea-kompis/component-action-list';
import { DoNotDiy } from '@inter-ikea-kompis/component-do-not-diy';
import { FunctionIcon, FunctionIconEnum } from '@inter-ikea-kompis/component-function-icon';
import { HomeDelivery } from '@inter-ikea-kompis/component-home-delivery';
import { Image } from '@inter-ikea-kompis/component-image';
import ImageAspectRatioEnum from '@inter-ikea-kompis/component-image/lib/enums/ImageAspectRatioEnum.js';
import { PriceModule, PriceModuleProductLinkEnum } from '@inter-ikea-kompis/component-price-module';
import { ProductInformationSection } from '@inter-ikea-kompis/component-product-information-section';
import { StoreSelector, StoreSelectorLayoutEnum } from '@inter-ikea-kompis/component-store-selector';
import { ZipIn, ZipInCardStateEnum } from '@inter-ikea-kompis/component-zip-in';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { Text } from '@ingka/common-styles-webc';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { ProductIdentifier as SkapaProductIdentifier } from '@ingka/product-identifier-webc/passive.js';
import { Status as SkapaStatus } from '@ingka/status-webc/passive.js';
import ProductCardMediaEnum from '../../enums/ProductCardMediaEnum.js';
import ProductCardModalEnum from '../../enums/ProductCardModalEnum.js';
import ProductCardPriceLayoutTypeEnum from '../../enums/ProductCardPriceLayoutTypeEnum.js';
import ProductCardMedia from '../product-card-media/ProductCardMedia.js';
import ProductCardWaterSenseImage from '../product-card-water-sense-image/ProductCardWaterSenseImage.js';
import InformationCardStyle from './InformationCardStyle.js';
/**
 * Product card information card component.
 *
 * @event modalClose Triggered when the modal should be closed.
 * @event modalOpen Triggered when a modal should be opened. "event.detail.visibleModal" tells you what modal that should be opened.
 * @event carouselChange Triggered when a carousel slide should be changed.
 * @event carouselShowAllThumbnails Triggered when the show all thumbnails button is clicked in the carousel.
 * @event storeSelectorChange Triggered when the user selects a store in the store selector.
 * @event homeDeliveryForgetZipCode User clicks on the forget link.
 * @event homeDeliverySaveZipCode User clicks on the save link.
 * @event homeDeliveryClick User clicks to open zip in sheet.
 */
export default class InformationCard extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Sets how media should be displayed.
        this.media = ProductCardMediaEnum.singleImage;
        // The aspect ratio that the images should adopt.
        this.imageAspectRatio = ImageAspectRatioEnum.ORIGINAL;
        // Defines the image sort order. When only one image is being displayed, the first image matching the type is used.
        this.imageSortOrder = null;
        // Selected index of the carousel slides.
        this.carouselSelectedIndex = 0;
        // Max limit of thumbnails to be displayed in the carousel.
        this.carouselThumbnailLimit = null;
        // Shows all thumbnails in the carousel when a limit has been set.
        this.carouselShowAll = false;
        // Show scrollbar in carousel.
        this.carouselShowScrollbar = false;
        // Show arrows in carousel.
        this.carouselShowArrows = false;
        this.hoverImageSortOrder = [
            ImageTypeNameEnum.functionalProductPicture,
            ImageTypeNameEnum.contextProductPicture
        ];
        // Optional array of function icons.
        this.functionIcons = null;
        // Price module layout type.
        this.priceModuleLayout = ProductCardPriceLayoutTypeEnum.default;
        // Show a modal of type ProductCardModalEnum.
        this.visibleModal = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Show price excl tax.
        this.showPriceExclTax = false;
        // Product measures.
        this.productMeasures = ProductDescriptionProductMeasureEnum.none;
        // Show text describing the color and/or material of the product.
        this.showDesignText = false;
        // Show product package weight info.
        this.showWeight = false;
        // Show product package measures.
        this.showPackageMeasures = false;
        // Optional array of type IProduct used for products included in the main product, i.e. child products. Needed for price to be calculated and displayed for an assembly product (ASP). Products included in all variations is needed when card should represent multiple variations of an assembly product.
        this.includedProducts = null;
        // Shows the Home delivery component.
        this.showHomeDelivery = false;
        // Zip availability for the product(s). Object or array. Array is used when card should represent multiple variations of a product (array with availability for all variations), an assembly product (ASP) (array of availabilty for all child items) or multiple variations of assembly products (array of availabilty for all child items of all variations).
        this.zipAvailability = null;
        // Card state. Use ZipInCardStateEnum from the Zip in component.
        this.homeDeliveryState = ZipInCardStateEnum.default;
        // Submitted and validated ZIP code for Home Delivery.
        this.homeDeliveryZipCode = null;
        // Store availability for the product(s). Object or array. Array is used when card should represent multiple variations of a product (array with availability for all variations), an assembly product (ASP) (array of availabilty for all child items) or multiple variations of assembly products (array of availabilty for all child items of all variations).
        this.storeAvailability = null;
        // Stores.
        this.stores = null;
        // Shows a product identifier if the layout is set to "information".
        this.showProductIdentifier = false;
        // Text for product description.
        this.productDescription = null;
        // Selected store id.
        this.storeId = null;
        // Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
        this.measurementSystem = null;
        // Hides the text describing the type of the product. Can be turned on when e.g. listing the same product type in a list to avoid redundant information. Please note that it should be clear to the user what type of products are listed when this is turned on.
        this.hideTypeName = false;
        this.isHovered = false;
        this.dexfSettings = null;
    }
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `${this.getCard()}`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-status', SkapaStatus);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-product-identifier', SkapaProductIdentifier);
        this.customElements.define('kompis-store-selector', StoreSelector);
        this.customElements.define('kompis-action-list', ActionList);
        this.customElements.define('kompis-action-list-item', ActionListItem);
        this.customElements.define('kompis-price-module', PriceModule);
        this.customElements.define('kompis-function-icon', FunctionIcon);
        this.customElements.define('kompis-zip-in', ZipIn);
        this.customElements.define('kompis-home-delivery', HomeDelivery);
        this.customElements.define('kompis-product-information-section', ProductInformationSection);
        this.customElements.define('kompis-image', Image);
        this.customElements.define('kompis-function-icon', FunctionIcon);
        this.customElements.define('kompis-do-not-diy', DoNotDiy);
        this.customElements.define('kompis-product-card-media', ProductCardMedia);
        this.customElements.define('kompis-product-card-water-sense-image', ProductCardWaterSenseImage);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return !!this.localizedInformation || !!(this.translations && this.settings);
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    getCard() {
        const zipIn = this.getZipIn();
        const homeDelivery = this.getHomeDelivery();
        const storeAvailability = this.getStoreAvailability();
        const hasSlotContent = !!this.querySelector('slot')?.assignedElements().length;
        const slotDivider = !hasSlotContent && (homeDelivery || storeAvailability)
            ? this.getDivider('slotDivider')
            : null;
        const homeDeliveryDivider = (this.showHomeDelivery || this.stores?.length) &&
            (this.getProductDescription() || this.getProductIdentifierItemNo())
            ? this.getDivider('homeDeliveryDivider')
            : null;
        const content = [
            this.getPriceModule(),
            this.getDoNotDiy(),
            this.getMedia(),
            this.getEnergyComplianceWaterSenseImage(),
            this.getFunctionIcons(),
            this.getActionListSlot(),
            slotDivider,
            zipIn,
            homeDelivery,
            storeAvailability,
            homeDeliveryDivider,
            this.getProductDescription(),
            this.getProductIdentifier(),
            this.getProductInformationSection()
        ];
        // eslint-disable-next-line lit-a11y/mouse-events-have-key-events
        return html `<style>
				${InformationCardStyle.getStyle({
            theme: this.theme,
            hasHomeDelivery: !!this.showHomeDelivery
        })}
			</style>
			<div
				class="container"
				@mouseover="${this.onMouseOver.bind(this)}"
				@mouseleave="${this.onMouseLeave.bind(this)}"
			>
				<div class="outline"></div>
				${content}
			</div>`;
    }
    getMedia() {
        return html `
			<kompis-product-card-media
				@carouselChange="${(event) => {
            this.dispatchEvent(new CustomEvent('carouselChange', { bubbles: true, detail: event.detail }));
        }}"
				@carouselShowAllThumbnails="${(event) => {
            this.dispatchEvent(new CustomEvent('carouselShowAllThumbnails', { bubbles: true, detail: event.detail }));
        }}"
				.theme="${this.theme}"
				.product="${this.product}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.media="${this.media}"
				.hovered="${this.isHovered}"
				.carouselSelectedIndex="${this.carouselSelectedIndex}"
				.carouselThumbnailLimit="${this.carouselThumbnailLimit}"
				.carouselShowAll="${this.carouselShowAll}"
				.carouselShowScrollbar="${this.carouselShowScrollbar}"
				.carouselShowArrows="${this.carouselShowArrows}"
				.imageAspectRatio="${this.imageAspectRatio}"
				.imageSortOrder="${this.imageSortOrder}"
				.hoverImageSortOrder="${this.hoverImageSortOrder}"
			></kompis-product-card-media>
		`;
    }
    getPriceModule() {
        const calculatePriceFromChildProducts = ProductUtility.shouldCalculatePriceFromChildProducts(this.product);
        return html `
			<div class="priceModuleWrapper">
				<kompis-price-module
					@modalOpen="${(event) => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: event.detail.visibleModal
            }
        }))}"
					@modalClose="${() => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }))}"
					.shoppingProducts="${this.getPriceModuleShoppingProducts()}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.settings="${this.settings}"
					.theme="${this.theme}"
					.layout="${this.priceModuleLayout}"
					.productName="${this.product.content?.name}"
					.productLink="${this.getProductLinkState()}"
					.visibleModal="${this.visibleModal}"
					.disableLinks="${this.disableLinks}"
					.showPriceUnit="${!calculatePriceFromChildProducts}"
					.showEcoFee="${true}"
					.showPriceExclTax="${this.showPriceExclTax}"
					.showEnergyLabel="${!calculatePriceFromChildProducts}"
					.showRepairabilityIndex="${!calculatePriceFromChildProducts}"
					.showLowestPreviousSalesPrice="${!calculatePriceFromChildProducts}"
					.isCombinedProduct="${calculatePriceFromChildProducts}"
				>
					${this.getDescription()}
				</kompis-price-module>
			</div>
		`;
    }
    getDoNotDiy() {
        return html `<kompis-do-not-diy .product="${this.product}"></kompis-do-not-diy>`;
    }
    getFunctionIcons() {
        if (!this.functionIcons) {
            return null;
        }
        return html `
			<div class="functionIcons">
				${this.functionIcons.map((icon) => html `
						<kompis-function-icon
							.theme="${this.theme}"
							.icon="${icon.icon}"
							.size="${FunctionIconEnum.small}"
							.label="${icon.title}"
						></kompis-function-icon>
					`)}
			</div>
		`;
    }
    getActionListSlot() {
        return html ` <slot @slotchange="${() => this.requestUpdate()}"></slot> `;
    }
    getEnergyComplianceWaterSenseImage() {
        return html `
			<kompis-product-card-water-sense-image
				.theme="${this.theme}"
				.product="${this.product}"
			></kompis-product-card-water-sense-image>
		`;
    }
    getProductDescription() {
        if (!this.productDescription) {
            return null;
        }
        return html `
			<div class="${`productDescription ${Text.classnames({ body: { m: true } })}`}">
				${this.productDescription}
			</div>
		`;
    }
    getDescription() {
        return ProductDescriptionUtility.getDescription({
            settings: this.getSettings(),
            translations: this.getTranslations(),
            product: this.product,
            include: {
                productMeasures: this.productMeasures,
                designText: this.showDesignText,
                packageMeasures: this.showPackageMeasures,
                weight: this.showWeight,
                measurementSystem: this.measurementSystem
            },
            exclude: {
                typeName: this.hideTypeName
            }
        });
    }
    getDivider(classname) {
        return html ` <div class="${classname} divider"></div> `;
    }
    getZipIn() {
        return html ` <kompis-zip-in
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.getTranslations()}"
			.visibleModal="${this.visibleModal}"
			.zipCode="${this.homeDeliveryZipCode}"
			.state="${this.homeDeliveryState}"
			.sheetAlignment="${SheetAlignmentEnum.right}"
			.settings="${this.getSettings()}"
			@forget="${(event) => {
            this.dispatchEvent(new CustomEvent('homeDeliveryForgetZipCode', { bubbles: true, detail: event.detail }));
        }}"
			@modalClose="${(event) => {
            this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }));
        }}"
			@save="${(event) => {
            this.dispatchEvent(new CustomEvent('homeDeliverySaveZipCode', { bubbles: true, detail: event.detail }));
        }}"
		></kompis-zip-in>`;
    }
    getHomeDelivery() {
        if (!this.showHomeDelivery) {
            return null;
        }
        return html ` <kompis-home-delivery
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.zipAvailabilities="${this.zipAvailability
            ? Array.isArray(this.zipAvailability)
                ? this.zipAvailability
                : [this.zipAvailability]
            : null}"
			.zipCode="${this.homeDeliveryZipCode}"
			@homeDeliveryClick="${() => {
            this.dispatchEvent(new CustomEvent('modalOpen', {
                bubbles: true,
                detail: {
                    visibleModal: ProductCardModalEnum.zipIn
                }
            }));
        }}"
		></kompis-home-delivery>`;
    }
    getStoreAvailability() {
        if (!this.stores?.length) {
            return null;
        }
        return html `
			<div class="storeAvailability">
				<kompis-store-selector
					@click="${(event) => event.stopPropagation()}"
					@modalOpen="${(event) => this.dispatchEvent(new CustomEvent('modalOpen', { bubbles: true, detail: event.detail }))}"
					@modalClose="${(event) => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }))}"
					@change="${(event) => {
            this.dispatchEvent(new CustomEvent('storeSelectorChange', {
                bubbles: true,
                detail: { ...event.detail, product: this.product }
            }));
        }}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.settings="${this.settings}"
					.theme="${this.theme}"
					.stores="${this.stores}"
					.storeId="${this.storeId}"
					.storeAvailabilities="${this.storeAvailability
            ? Array.isArray(this.storeAvailability)
                ? this.storeAvailability
                : [this.storeAvailability]
            : null}"
					.visibleModal="${this.visibleModal}"
					.layout="${StoreSelectorLayoutEnum.link}"
				></kompis-store-selector>
			</div>
		`;
    }
    getProductIdentifierItemNo() {
        const itemNo = this.product.content?.ruItemNo;
        if (!this.showProductIdentifier || !itemNo) {
            return null;
        }
        return itemNo;
    }
    /**
     * Some products (DEXF custom items) are missing "ruItemNo".
     *
     * @returns Any.
     */
    getProductIdentifier() {
        const itemNo = this.getProductIdentifierItemNo();
        if (itemNo) {
            return html `
				<div class="productIdentifier">
					<skapa-product-identifier label="${this.getTranslations().productArticleNo}">
						${ProductUtility.formatItemNo(itemNo)}
					</skapa-product-identifier>
				</div>
			`;
        }
        return null;
    }
    getPriceModuleShoppingProducts() {
        if (ProductUtility.shouldCalculatePriceFromChildProducts(this.product)) {
            const includedShoppingProducts = ProductUtility.getIncludedShoppingProducts({
                product: this.product,
                includedProducts: this.includedProducts ?? [],
                ignoreInvalidProducts: true
            });
            if (includedShoppingProducts.length > 0 &&
                includedShoppingProducts.length === this.product.content?.child?.length) {
                return includedShoppingProducts;
            }
        }
        return [{ quantity: 1, product: this.product }];
    }
    getProductLinkState() {
        const itemTypesWithLink = [ItemTypeEnum.art, ItemTypeEnum.spr];
        const productItemType = this.product.content?.itemType;
        if ((productItemType && !itemTypesWithLink.includes(productItemType)) || this.disableLinks) {
            return PriceModuleProductLinkEnum.disabled;
        }
        return PriceModuleProductLinkEnum.productInformationPage;
    }
    getProductInformationSection() {
        if (!this.product.content?.complianceTechnical &&
            !this.product.content?.goodToKnow &&
            !this.product.content?.customerBenefit) {
            return null;
        }
        return html `
			<kompis-product-information-section
				.theme="${this.theme}"
				.product="${this.product}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.visibleModal="${this.visibleModal}"
				.measurementSystem="${this.measurementSystem}"
				@modalOpen="${(event) => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: event.detail.visibleModal
            }
        }))}"
				@modalClose="${(event) => {
            this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }));
            event.detail?.triggerElement?.focus();
        }}"
			>
			</kompis-product-information-section>
		`;
    }
    onMouseOver() {
        if (this.isHovered || this.disableLinks) {
            return;
        }
        this.isHovered = true;
        this.requestUpdate();
    }
    onMouseLeave() {
        if (!this.isHovered || this.disableLinks) {
            return;
        }
        this.isHovered = false;
        this.requestUpdate();
    }
}
__decorate([
    property({ type: Object, required: true })
], InformationCard.prototype, "theme", void 0);
__decorate([
    property({ type: Object, required: true })
], InformationCard.prototype, "product", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "settings", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "media", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "imageAspectRatio", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "imageSortOrder", void 0);
__decorate([
    property({ type: Number })
], InformationCard.prototype, "carouselSelectedIndex", void 0);
__decorate([
    property({ type: Number })
], InformationCard.prototype, "carouselThumbnailLimit", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "carouselShowAll", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "carouselShowScrollbar", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "carouselShowArrows", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "hoverImageSortOrder", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "functionIcons", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "priceModuleLayout", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "showPriceExclTax", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "productMeasures", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "showDesignText", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "showWeight", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "showPackageMeasures", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "includedProducts", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "showHomeDelivery", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "zipAvailability", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "homeDeliveryState", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "homeDeliveryZipCode", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "storeAvailability", void 0);
__decorate([
    property({ type: Object })
], InformationCard.prototype, "stores", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "showProductIdentifier", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "productDescription", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "storeId", void 0);
__decorate([
    property({ type: String })
], InformationCard.prototype, "measurementSystem", void 0);
__decorate([
    property({ type: Boolean })
], InformationCard.prototype, "hideTypeName", void 0);
//# sourceMappingURL=InformationCard.js.map